import { Box, Text, chakra, Flex, Input, Button } from "@chakra-ui/react";
import { ErrorMessage, Field } from "formik";
import { Link } from "react-router-dom";

const StyledInputField = chakra(Field, {
  baseStyle: {
    border: "2px solid",
    borderColor: "gray.200",
    _focus: {
      borderColor: "primary.link",
    },
    fontSize: "14px",
    color: "black.100",
    fontWeight: "400",
    pl: "16px",
    borderRadius: "12px",
    size: "full",
    _dark: {
      backgroundColor: "primary.inverse.gray",
      borderColor: "primary.inverse.gray",
      color: "primary.shineGrayText",
    },
    _light: {
      backgroundColor: "transparent",
    },
  },
});

const StyledCheckboxText = chakra(Text, {
  baseStyle: {
    fontWeight: "400",
    color: "primary.grayText",
    fontSize: "14px",
  },
});

const LinkText = chakra(Link, {
  baseStyle: {
    fontWeight: "500",
    color: "gray.neutral",
    fontSize: "16px",
  },
});

// Auth Form Container with Ellipse
export const AuthFormContainer = chakra(Box, {
  baseStyle: {
    display: "flex",
    padding: "44px 26px 26px 26px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "16px",
    borderRadius: "20px",
    border: "4px solid #FFF",
    background: "#FFF",
    boxShadow: `
      0px 3px 11px 0px rgba(24, 25, 180, 0.06),
      0px 10px 18px 0px rgba(24, 25, 180, 0.06)
    `,
    position: "relative",
    overflow: "hidden",
    mx: "auto",
    //my: "auto",
    maxW: "500px",
    width: "100%",
  },
});

export const AuthCardEllipse = chakra(Box, {
  baseStyle: {
    width: "474px",
    height: "474px",
    position: "absolute",
    top: "-237px", // half of the height (474/2), to hide upper half
    left: "50%", // center horizontally
    transform: "translateX(-50%)", // adjust for true center
    borderRadius: "50%",
    opacity: 0.2,
    background:
      "linear-gradient(180deg, #1819B4 64.45%, rgba(24, 25, 180, 0.00) 106.75%)",
    filter: "blur(77.17774963378906px)",
    pointerEvents: "none",
    zIndex: 0,
  },
});

// Auth Card Top Frame
export const AuthCardTopFrame = chakra(Flex, {
  baseStyle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "15px",
    alignSelf: "stretch",
    width: "100%",
  },
});

// KPEC Logo Text
export const KpecLogoText = chakra(Text, {
  baseStyle: {
    color: "#1819B4",
    WebkitTextStrokeWidth: "0.56px",
    WebkitTextStrokeColor: "#1819B4",
    fontFamily: "sans-serif",
    fontSize: "32px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
    textAlign: "center",
  },
});

// Auth Heading
export const AuthHeading = chakra(Text, {
  baseStyle: {
    color: "#111827",
    textAlign: "center",
    fontFamily: "Poppins",
    fontSize: "32px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    alignSelf: "stretch",
  },
});

// Auth Subheading
export const AuthSubheading = chakra(Text, {
  baseStyle: {
    color: "#798692",
    textAlign: "left",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
    alignSelf: "stretch",
  },
});

// Auth Fields Wrapper
export const AuthFieldsWrapper = chakra(Flex, {
  baseStyle: {
    direction: "column",
    gap: "1.5rem",
    width: "100%",
  },
});

// Auth Input Box
export const AuthInputBox = chakra(Box, {
  baseStyle: {
    height: "40px",
    borderRadius: "8px",
    borderColor: "#CDD6DC",
    background: "#FFF",
    mb: 0,
    width: "100%",
  },
});

// Auth Input Field
export const AuthInputField = chakra(Input, {
  baseStyle: {
    width: "100%",
    height: "40px",
    borderRadius: "8px",
    borderColor: "#CDD6DC",
    background: "#FFF",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: 400,
    _focus: {
      borderColor: "#1819B4",
      boxShadow: "0 0 0 1px #1819B4",
    },
    _placeholder: {
      color: "#9CA3AF",
    },
  },
});

// Auth Label Text
export const AuthLabelText = chakra(Text, {
  baseStyle: {
    alignSelf: "stretch",
    color: "#2F2F34",
    fontFamily: "Poppins",
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "normal",
  },
});

// Auth Button
export const AuthButton = chakra(Button, {
  baseStyle: {
    display: "flex",
    height: "48px",
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "stretch",
    borderRadius: "16px",
    background: "#1819B4",
    color: "#FFF",
    textAlign: "center",
    fontFamily: "Poppins",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    _hover: {
      background: "#1415A0",
    },
    _active: {
      background: "#0F1080",
    },
    _disabled: {
      opacity: 0.6,
      cursor: "not-allowed",
      pointerEvents: "none",
      background: "#1819B4", // Keep the same background color
      _hover: {
        background: "#1819B4", // Prevent hover color change when disabled
        opacity: 0.6,
      },
      _active: {
        background: "#1819B4", // Prevent active color change when disabled
        opacity: 0.6,
      },
    },
    _loading: {
      opacity: 1, // Keep full opacity when loading (spinner should be visible)
      cursor: "wait",
      pointerEvents: "none",
      background: "#1819B4", // Keep the same background color
    },
  },
});

// Auth Button Wrapper
export const AuthButtonWrapper = chakra(Flex, {
  baseStyle: {
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    mt: "1.5rem",
    gap: "10px",
  },
});

// Auth Link Text
export const AuthLinkText = chakra(Text, {
  baseStyle: {
    color: "#1819B4",
    textAlign: "right",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "normal",
    cursor: "pointer",
    _hover: {
      textDecoration: "underline",
    },
  },
});

// Auth Checkbox Text
export const AuthCheckboxText = chakra(Text, {
  baseStyle: {
    color: "#2F2F34",
    fontFamily: "Poppins",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "normal",
  },
});

export const AddNoticeButton = chakra(Button, {
  baseStyle: {
    display: "flex",
    height: "48px",
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "stretch",
    borderRadius: "16px",
    background: "#1819B4",
    color: "#FFF",
    textAlign: "center",
    fontFamily: "Poppins",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    width: "160px",
    _hover: {
      background: "#1415A0",
    },
    _active: {
      background: "#0F1080",
    },
    _disabled: {
      opacity: 0.6,
      cursor: "not-allowed",
      pointerEvents: "none",
      background: "#1819B4",
      _hover: {
        background: "#1819B4",
        opacity: 0.6,
      },
      _active: {
        background: "#1819B4",
        opacity: 0.6,
      },
    },
    _loading: {
      opacity: 1,
      cursor: "wait",
      pointerEvents: "none",
      background: "#1819B4",
    },
  },
});

const ForgotPasswordContainer = chakra(Box, {
  baseStyle: {
    width: "full",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    pt: "6px",
    background:
      "linear-gradient(0deg, rgba(167, 187, 200, 0.12) 0%, rgba(167, 187, 200, 0.12) 100%), #FFF",
  },
});

const AllSetContainer = chakra(Box, {
  baseStyle: {
    background: "linear-gradient(180deg, #C2D9EF 0%, #FFFFFF 100%)",
    width: "full",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
    pt: "6px",
  },
});
const FooterContainer = chakra(Box, {
  baseStyle: {
    width: "full",
    bg: "white",
    marginTop: "200px",
  },
});

const LogoContainer = chakra(Box, {
  baseStyle: {
    p: 4,
  },
});

const ErrorMessageText = chakra(ErrorMessage, {
  baseStyle: {
    fontWeight: "300",
    fontSize: "14px",
    color: "red",
  },
});

export {
  LinkText,
  StyledCheckboxText,
  StyledInputField,
  ForgotPasswordContainer,
  FooterContainer,
  LogoContainer,
  ErrorMessageText,
  AllSetContainer,
};
